import {
  useDistributorEmployeeById,
  useLogout,
  useUser,
} from "creso-core/hooks";
import { User, DistributorEmployee } from "creso-core/types";
import { createContext, useCallback, useContext, useMemo } from "react";
import { Navigate } from "@tanstack/react-router";

interface AuthContextType {
  user: User;
  logout: () => Promise<void>;
  employee: DistributorEmployee | null;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function useAuthContext() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuthContext must be used within an AuthProvider");
  }
  return context;
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { data: user, isPending, isLoading, error, refetch } = useUser();
  const employeeId = useMemo(() => {
    if (!user || user.user_type !== "employee") return undefined;
    if (!user.metadata || typeof user.metadata !== "object") return undefined;

    const metadata = user.metadata as Record<string, unknown>;
    const rawId = metadata.id ?? null;

    if (typeof rawId === "number") return rawId;
    if (typeof rawId === "string") {
      const parsed = Number(rawId);
      return Number.isNaN(parsed) ? undefined : parsed;
    }
    return undefined;
  }, [user]);

  const {
    data: employee,
    isLoading: isEmployeeLoading,
    error: employeeError,
  } = useDistributorEmployeeById(employeeId, {
    enabled: user?.user_type === "employee" && employeeId != null,
  });

  const { mutateAsync } = useLogout();

  const logout = useCallback(async () => {
    await mutateAsync();
    await refetch();
  }, [mutateAsync, refetch]);

  if (isPending || isLoading || isEmployeeLoading) {
    return null;
  }

  if (error || employeeError || !user) {
    return <Navigate to="/login/distributor" />;
  }

  return (
    <AuthContext.Provider value={{ user, logout, employee: employee ?? null }}>
      {children}
    </AuthContext.Provider>
  );
}
